<template>
  <div>
    <mia-car-edit-shared-page-header />

    <mia-car-edit-notice
      class="mt-4"
      permanent
    >
      <p>Bitte wählen Sie die Flags aus, die Sie am Fahrzeug hinterlegen möchten.</p>
      <p class="my-0">
        Klicken Sie dazu auf die einzelnen Flags um sie aus- oder abzuwählen. Um die ausgewählten Flags am Fahrzeug zu speichern, klicken Sie anschließend auf den <b>Speichern</b> Button.
      </p>
    </mia-car-edit-notice>

    <mia-form
      class="mt-8"
      @submit="save"
    >
      <template #default>
        <mia-form-section
          title="Flags auswählen"
          vertical
        >
          <div class="grid grid-cols-2 divide-x divide-gray-400">
            <div class="pr-4">
              <h4 class="py-0 my-0 text-base font-bold tracking-wide text-gray-700">
                <span>Verfügbare Flags</span>
                <button
                  class="ml-3 text-sm text-orange-mia focus:outline-none"
                  :class="{
                    'hover:underline': availableFlags.length > 0,
                    'opacity-50 cursor-not-allowed': availableFlags.length === 0,
                  }"
                  type="button"
                  :disabled="availableFlags.length === 0"
                  @click="selectAll"
                >
                  alle auswählen
                </button>
              </h4>

              <mia-flags-list
                :flags="availableFlags"
                class="mt-4"
                :car="car"
                @toggle="addFlag"
              />
            </div>
            <div class="pl-4">
              <h4 class="py-0 my-0 text-base font-bold tracking-wide text-gray-700">
                <span>Flags am Fahrzeug</span>
                <button
                  class="ml-3 text-sm text-orange-mia focus:outline-none"
                  :class="{
                    'hover:underline': sortedSelectedFlags.length > 0,
                    'opacity-50 cursor-not-allowed': sortedSelectedFlags.length === 0,
                  }"
                  type="button"
                  :disabled="sortedSelectedFlags.length === 0"
                  @click="removeAll"
                >
                  alle entfernen
                </button>
              </h4>

              <mia-flags-list
                :flags="sortedSelectedFlags"
                class="mt-4"
                :car="car"
                @toggle="removeFlag"
              />
            </div>
          </div>
        </mia-form-section>
      </template>

      <template #actions>
        <mia-submit-button
          label="Speichern"
          :disabled="$wait.any"
          :is-loading="$wait.is('updating flags')"
        />
      </template>
    </mia-form>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy'
import { getCar } from '@/api/car'
import { updateFlags } from '@/api/cars'
import { getAvailableCarFlags } from '@/api/flags'
import FlagsList from '@/components/cars/flags/FlagsList.vue'
import SharedPageHeader from '@/components/cars/edit/SharedPageHeader.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'

export default {
  name: 'CarEditFlags',

  components: {
    'mia-car-edit-shared-page-header': SharedPageHeader,
    'mia-flags-list': FlagsList,
    'mia-car-edit-notice': Notice,
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-submit-button': SubmitButton
  },

  data: () => ({
    allFlags: [],
    selectedFlags: []
  }),

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    availableFlags() {
      return sortBy(this.allFlags.filter(flag => !this.selectedFlags.find(f => f.id === flag.id)), 'item_title')
    },

    sortedSelectedFlags() {
      return sortBy(this.selectedFlags, 'item_title')
    }
  },

  watch: {
    car: {
      immediate: true,
      handler(car) {
        this.selectedFlags = car.flags || []
      }
    }
  },

  created() {
    this.fetchAvailableCarFlags()
  },

  methods: {
    async fetchAvailableCarFlags() {
      const [errors, response] = await getAvailableCarFlags(this.car.id)

      if (errors) {
        window.eventBus.$emit('mia-error', {
          error: ['Die Flags für dieses Fahrzeug konnten nicht geladen werden.']
        })
        return
      }

      this.allFlags = response.data
    },

    async save() {
      this.$wait.start('updating flags')
      const [errors] = await updateFlags(this.car.id, this.selectedFlags.map(flag => flag.id))
      this.$wait.end('updating flags')

      if (errors) {
        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich gespeichert.']
      })

      this.car = await getCar({
        carId: this.car.id
      })
    },

    addFlag(flag) {
      this.selectedFlags.push(flag)
    },

    removeFlag(flag) {
      this.selectedFlags = this.selectedFlags.filter(f => f.id !== flag.id)
    },

    selectAll() {
      this.selectedFlags = [...this.allFlags]
    },

    removeAll() {
      this.selectedFlags = []
    }
  }
}
</script>
